import { useEffect, useState } from "react";
import axios from "axios";
import { Eye, Users, MessageSquare, Receipt } from "lucide-react";
import { Card } from "./ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AnalyticsTab = ({ auth }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/analytics`, auth).then((r) => setData(r.data)).catch(() => setData({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return <p className="p-6 font-mono2 text-xs uppercase tracking-widest text-white/50">Loading analytics...</p>;

  const stats = [
    { label: "Total Page Views", value: data.total_views ?? 0, icon: Eye, testid: "stat-total-views", bg: "bg-staxyellow" },
    { label: "Views Today", value: data.views_today ?? 0, icon: Eye, testid: "stat-views-today", bg: "bg-staxcyan" },
    { label: "Total Leads", value: data.leads_total ?? 0, icon: Users, testid: "stat-leads", bg: "bg-staxmagenta text-white" },
    { label: "Open Chats", value: data.chats_open ?? 0, icon: MessageSquare, testid: "stat-chats", bg: "bg-ink text-white" },
  ];
  const series = data.series || [];
  const max = Math.max(1, ...series.map((d) => d.n));

  return (
    <div className="space-y-8" data-testid="analytics-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.testid} data-testid={s.testid} className={`border-4 border-white/90 ${s.bg} text-ink p-5 shadow-hard-yellow`}>
            <s.icon size={20} className={s.bg.includes("text-white") ? "text-white" : "text-ink"} />
            <p className={`mt-3 font-display text-4xl ${s.bg.includes("text-white") ? "text-white" : "text-ink"}`}>{s.value}</p>
            <p className={`font-mono2 text-[10px] font-bold uppercase tracking-[0.2em] ${s.bg.includes("text-white") ? "text-white/70" : "text-ink/50"}`}>{s.label}</p>
          </div>
        ))}
      </div>

      <Card title="Last 14 Days" color="bg-staxcyan" testid="analytics-chart">
        <div className="flex items-end gap-1.5 h-40">
          {series.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
              <span className="font-mono2 text-[9px] font-bold text-ink/60">{d.n || ""}</span>
              <div
                className="w-full bg-staxmagenta border border-ink"
                style={{ height: `${Math.max(2, (d.n / max) * 100)}%` }}
                title={`${d.day}: ${d.n} views`}
              />
              <span className="font-mono2 text-[8px] text-ink/40">{d.day.slice(5)}</span>
            </div>
          ))}
          {series.length === 0 && <p className="font-mono2 text-xs uppercase tracking-widest text-ink/40">No visits tracked yet.</p>}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card title="Traffic Sources" color="bg-staxyellow" testid="analytics-referrers">
          {(data.referrers || []).length === 0 && (
            <p className="font-mono2 text-xs uppercase tracking-widest text-ink/40">No traffic yet.</p>
          )}
          <ul className="space-y-2">
            {(data.referrers || []).map((r) => (
              <li key={r.source} className="flex items-center justify-between border-2 border-ink/15 bg-white px-4 py-2.5">
                <span className="font-semibold text-sm">{r.source}</span>
                <span className="font-display text-lg">{r.n}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card title="Leads By Type" color="bg-staxmagenta" testid="analytics-lead-types">
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.leads_by_type || {}).map(([t, n]) => (
              <span key={t} className="border-2 border-ink bg-white px-3 py-1.5 font-mono2 text-xs font-bold uppercase tracking-widest">
                {t.replace("_", " ")}: {n}
              </span>
            ))}
            {Object.keys(data.leads_by_type || {}).length === 0 && (
              <p className="font-mono2 text-xs uppercase tracking-widest text-ink/40">No leads yet.</p>
            )}
          </div>
          {(data.invoices_awaiting_payment ?? 0) > 0 && (
            <p className="mt-4 flex items-center gap-2 font-mono2 text-xs uppercase tracking-widest text-ink/60">
              <Receipt size={14} /> {data.invoices_awaiting_payment} invoice(s) awaiting payment
            </p>
          )}
        </Card>
      </div>
    </div>
  );
};
